import { useEffect, useRef, useState } from "react"
import Api from "../api/api"
import { Product } from "../api/models"
import { requiredMark } from "./InstallerFields"
import { ImageFieldView, useImageField } from "./ImageField"
import { LOGO_FIELD_HINT, useProductLogoField } from "./LogoPlacementDialog"
import Spinner from "./widgets/Spinner"
import { optionalDollarAmount, optionalNonNegativeInt } from "./AddProductDialog"
import { backdropDismiss } from "../utils/backdropDismiss"

// Edit the product's surface — name, URL, value, trial, pay-as-you-go flag,
// the screenshot and the logo (replace via presign→PUT like
// AddProductDialog, or remove; a replacement logo goes through the
// placement editor). Owner is write-once (the upload namespace) and the AL config is
// not editable here. Sends a minimal diff patch and skips the call when
// nothing changed. Conditionally mounted (OfferingDialog pattern).
export default function EditProductDialog({ product, accessToken, onClose, onSaved }: {
  product: Product
  accessToken: string
  onClose: () => void
  onSaved: (product: Product) => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  const [name, setName] = useState(product.name)
  const [url, setUrl] = useState(product.url ?? "")
  // valueCents arrives 0 for "no value" (the wire collapses null to 0) — an
  // empty field and 0 mean the same thing on save
  const [value, setValue] = useState(product.valueCents > 0 ? String(product.valueCents / 100) : "")
  const [needsCredits, setNeedsCredits] = useState(product.needsCredits)
  const [trialDays, setTrialDays] = useState(product.trialDays !== undefined ? String(product.trialDays) : "")
  const [trialMaxActivations, setTrialMaxActivations] = useState(product.trialMaxActivations !== undefined ? String(product.trialMaxActivations) : "")
  // remove the existing image / logo on save (moot once a replacement is
  // staged)
  const [imageRemoved, setImageRemoved] = useState(false)
  const [logoRemoved, setLogoRemoved] = useState(false)
  const [step, setStep] = useState<"form" | "saving" | "error">("form")
  const [errorMessage, setErrorMessage] = useState("")
  const screenshot = useImageField(accessToken, (width, productId) =>
    Api.createProductImageUpload({ productId, width, accessToken }))
  const { logo, editor: logoEditor, adjust: adjustLogo } = useProductLogoField({
    accessToken, ownerId: product.ownerId, excludeProductId: product.id
  })

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const formValid =
    name.trim() !== ""
    && name.trim().length <= 64
    && (url.trim() === "" || url.trim().startsWith("https://"))
    && optionalDollarAmount(value)
    && optionalNonNegativeInt(trialDays)
    && optionalNonNegativeInt(trialMaxActivations)

  const handleSubmit = async () => {
    if (!formValid) return
    setStep("saving")
    setErrorMessage("")
    try {
      const patch: Parameters<typeof Api.updateProduct>[0]["patch"] = {}
      if (name.trim() !== product.name) patch.name = name.trim()
      if ((url.trim() || null) !== (product.url ?? null)) patch.url = url.trim() || null
      const valueCents = value.trim() !== "" ? Math.round(Number(value) * 100) : null
      if ((valueCents ?? 0) !== product.valueCents) patch.valueCents = valueCents
      if (needsCredits !== product.needsCredits) patch.needsCredits = needsCredits
      const days = trialDays.trim() !== "" ? Number(trialDays) : null
      if (days !== (product.trialDays ?? null)) patch.trialDays = days
      const activations = trialMaxActivations.trim() !== "" ? Number(trialMaxActivations) : null
      if (activations !== (product.trialMaxActivations ?? null)) patch.trialMaxActivations = activations
      if (screenshot.file) {
        const { width, rev } = await screenshot.upload(product.id)
        patch.imageWidth = width
        patch.imageRev = rev
      } else if (imageRemoved && product.imageUrl) {
        patch.imageWidth = null
      }
      if (logo.file) {
        const { width, rev } = await logo.upload(product.id)
        patch.logoWidth = width
        patch.logoRev = rev
      } else if (logoRemoved && product.logoUrl) {
        patch.logoWidth = null
      }
      if (Object.keys(patch).length > 0) {
        const { product: updated } = await Api.updateProduct({ productId: product.id, patch, accessToken })
        onSaved(updated)
      } else {
        onSaved(product)
      }
      dialogRef.current?.close()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  const busy = step === "saving"

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content no-scrollbar" style={{ maxHeight: "85vh", overflowY: "auto" }}>
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">{product.name}</h2>

        <div className="generate-codes-form">
          <label className="generate-codes-field">
            <span className="generate-codes-label">Name{requiredMark}</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="generate-codes-input"
              disabled={busy}
              maxLength={64}
              autoFocus
            />
          </label>

          <label className="generate-codes-field">
            <span className="generate-codes-label">Product page URL</span>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="generate-codes-input"
              disabled={busy}
              placeholder="https://…"
            />
            <span style={{
              fontSize: "9pt",
              minHeight: "1.3em",
              color: url.trim() !== "" && !url.trim().startsWith("https://") ? "#e07470" : "var(--nfa-light-grey)"
            }}>
              {url.trim() !== "" && !url.trim().startsWith("https://")
                ? "The URL must start with https:// — or be empty."
                : "Optional — the public product page link."}
            </span>
          </label>

          <label className="generate-codes-field">
            <span className="generate-codes-label">Value ($)</span>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="generate-codes-input"
              disabled={busy}
              placeholder="empty: no standalone value"
            />
            {!optionalDollarAmount(value) && (
              <span style={{ fontSize: "9pt", color: "#e07470" }}>
                Dollars with up to two decimals — or empty.
              </span>
            )}
          </label>

          <div style={{ display: "flex", gap: 16 }}>
            <label className="generate-codes-field" style={{ flex: 1 }}>
              <span className="generate-codes-label">Trial days</span>
              <input
                type="text"
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
                className="generate-codes-input"
                disabled={busy}
                placeholder="empty: no trial"
              />
            </label>
            <label className="generate-codes-field" style={{ flex: 1 }}>
              <span className="generate-codes-label">Trial activations</span>
              <input
                type="text"
                value={trialMaxActivations}
                onChange={(e) => setTrialMaxActivations(e.target.value)}
                className="generate-codes-input"
                disabled={busy}
              />
            </label>
          </div>
          {(!optionalNonNegativeInt(trialDays) || !optionalNonNegativeInt(trialMaxActivations)) && (
            <span style={{ fontSize: "9pt", color: "#e07470" }}>
              Trial fields must be non-negative whole numbers — or empty.
            </span>
          )}

          <label className="generate-codes-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={needsCredits}
              onChange={(e) => setNeedsCredits(e.target.checked)}
              disabled={busy}
            />
            <span className="generate-codes-label" style={{ margin: 0 }}>
              Pay-as-you-go (runs on credits)
            </span>
          </label>

          <ImageFieldView
            fields={screenshot}
            busy={busy}
            current={{
              imageUrl: product.imageUrl,
              removed: imageRemoved,
              onRemove: () => setImageRemoved(true),
              onRestore: () => setImageRemoved(false)
            }}
          />
          <ImageFieldView
            fields={logo}
            busy={busy}
            label="Logo (PNG, optional)"
            hint={LOGO_FIELD_HINT}
            variant="logo"
            onAdjust={adjustLogo}
            current={{
              imageUrl: product.logoUrl,
              removed: logoRemoved,
              onRemove: () => setLogoRemoved(true),
              onRestore: () => setLogoRemoved(false)
            }}
          />
        </div>

        {step === "error" && errorMessage && (
          <p className="generate-codes-error">{errorMessage}</p>
        )}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
          <button
            className="btn-upgrade"
            disabled={!formValid || busy}
            onClick={handleSubmit}
          >
            {busy ? <><Spinner size={12} /> Saving…</> : "Save"}
          </button>
        </div>
      </div>
      {logoEditor}
    </dialog>
  )
}

// Permanently delete a product. Only possible when nothing references it —
// the backend 409s with the list of blockers (SKU version claims, licenses,
// trials, credits history, A.L. usage) and that message is surfaced here.
// Uploaded images stay on the bucket. Conditionally mounted (the
// DeleteSkuDialog pattern).
export function DeleteProductDialog({ product, accessToken, onClose, onDeleted }: {
  product: Product
  accessToken: string
  onClose: () => void
  onDeleted: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [step, setStep] = useState<"confirm" | "deleting" | "error">("confirm")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const handleDelete = async () => {
    setStep("deleting")
    setErrorMessage("")
    try {
      await Api.deleteProduct({ productId: product.id, accessToken })
      onDeleted()
      dialogRef.current?.close()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (step !== "deleting") dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Delete Product</h2>

        <p>
          Permanently delete {product.name}? Its A.L. configuration is
          deleted with it. Uploaded images stay on the downloads bucket.
        </p>
        <p>
          This only works for a product nothing references: not claimed by
          any SKU version, and no licenses, trials, credits history or A.L.
          usage. Otherwise the product is kept and the blockers will be
          listed below.
        </p>

        {step === "error" && errorMessage && (
          <p className="generate-codes-error">{errorMessage}</p>
        )}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={step === "deleting"}>Cancel</button>
          <button
            className="btn-upgrade"
            disabled={step === "deleting"}
            onClick={handleDelete}
          >
            {step === "deleting" ? <><Spinner size={12} /> Trying…</> : "Try Deleting"}
          </button>
        </div>
      </div>
    </dialog>
  )
}
